import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  deleteAirline,
  fetchAirlines,
  refreshAirlineList,
} from "src/store/reducers/airlines";
import ConfirmationModal from "src/layouts/modals/ConfirmationModal";
import { toast } from "react-toastify";
import AirlineFormCanvas from "./AirlineFormCanvas";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import BottomPagination from "src/jsx/customComponents/pagination/BottomPagination";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";

const ListAirlines = () => {
  const dispatch = useAppDispatch();
  const airlineList = useAppSelector((state) => state.airlines.data);
  const airlineListStatus = useAppSelector((state) => state.airlines.status);
  const [editId, setEditId] = useState("");
  const [deleteAirlineId, setDeleteAirlineId] = useState<string>();
  const [page, setPage] = useState(1);

  const [pending, setPending] = useState(false);
  const keywordRef = useRef("");

  const {
    handleChange,
    values,
    handleSubmit,
  } = useFormik({
    initialValues: {
      keyword: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      keyword: Yup.string(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      keywordRef.current = values.keyword;
      setPage(1);
      dispatch(fetchAirlines({ page: 1, keyword: values.keyword }));
      setPending(false);
    },
  });

  const retry = useCallback(() => {
    dispatch(fetchAirlines({ page, keyword: keywordRef.current }));
  }, [dispatch, page]);

  const cancelDelete = () => {
    setDeleteAirlineId(undefined);
  };

  const confirmDelete = useCallback(async () => {
    if (deleteAirlineId) {
      try {
        const data = await dispatch(deleteAirline(deleteAirlineId)).unwrap();
        toast.success("Airline is deleted successfully");
        setDeleteAirlineId(undefined);
        setPage(1);
        retry();
      } catch (e: any) {
        FormikAPIErrorHandler(e.message, undefined, toast.error);
      }
    }
  }, [deleteAirlineId, dispatch, retry]);

  useEffect(() => {
    if (airlineListStatus === "idle") {
      retry();
    }
  }, [airlineListStatus, retry]);

  useEffect(() => {
    return () => {
      dispatch(refreshAirlineList());
    };
  }, [dispatch]);

  const setPageAndFetch = useCallback(
    (newPage: number) => {
      setPage(newPage);
      dispatch(fetchAirlines({ page: newPage, keyword: keywordRef.current }));
    },
    [dispatch]
  );

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-xxl-12">
            <div className="card h-auto">
              <form onSubmit={handleSubmit}>
                <div className="row container-fluid">
                  <div className="col-xl-3 mb-3">
                    <CustomInputWithLabel
                      id="keyword"
                      placeholder={"Search"}
                      handleChange={handleChange}
                      value={values.keyword}
                    />
                  </div>
                  <div className="col-xl-2 mb-3">
                    <Button
                      className="form-control"
                      disabled={pending}
                      type="submit"
                    >
                      {pending ? "Loading" : "Submit"}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-xxl-12">
            <div className="card h-auto">
              <div className="card-header">
                <h4 className="heading mb-0">Airlines</h4>
                <Link
                  to={"#"}
                  className="btn btn-primary btn-sm"
                  data-bs-toggle="offcanvas"
                  onClick={() => setEditId("Add")}
                >
                  + Add Airline
                </Link>
              </div>
              <div className="card-body ">
                {airlineListStatus === "success" ? (
                  airlineList?.results && airlineList?.results.length > 0 ? (
                    <div className="table-responsive active-projects">
                      <div
                        id="projects-tbl_wrapper"
                        className="dataTables_wrapper no-footer"
                      >
                        <table
                          id="projects-tbl"
                          className="table table-hover dataTable no-footer mb-0"
                        >
                          <thead>
                            <tr>
                              <th>Airline Name</th>
                              <th>Airline Code</th>
                              <th>Contact Person</th>
                              <th>Contact No.</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {airlineList?.results?.map((airline, index) => (
                              <tr key={airline.id}>
                                <td>{airline.airline_name}</td>
                                <td>{airline.airline_code}</td>
                                <td>{airline.contact_person_name}</td>
                                <td>{airline.contact_number}</td>
                                <td>
                                  <NavLink
                                    className="btn btn-warning shadow btn-xs sharp me-1"
                                    to={`/airlines/view/${airline.id}`}
                                    title="View"
                                  >
                                    <i className={`fas fa-paper-plane`} />
                                  </NavLink>{" "}
                                  <NavLink
                                    className="btn btn-primary shadow btn-xs sharp me-1"
                                    onClick={() => setEditId(airline.id)}
                                    to={"#"}
                                    title="Edit"
                                  >
                                    <i className={`fas fa-pen`} />
                                  </NavLink>{" "}
                                  <NavLink
                                    className="btn btn-danger shadow btn-xs sharp"
                                    onClick={() => {
                                      setDeleteAirlineId(airline.id);
                                    }}
                                    to={`#`}
                                    title="Delete"
                                  >
                                    <i className={`fas fa-x`} />
                                  </NavLink>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {airlineList?.count ? (
                          <BottomPagination
                            count={airlineList?.count}
                            page={page}
                            setPageAndFetch={setPageAndFetch}
                          />
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <strong>NA</strong>
                    </div>
                  )
                ) : airlineListStatus === "pending" ? (
                  <div className="d-flex align-items-center">
                    <strong>Loading...</strong>
                    <div
                      className="spinner-border ms-auto"
                      role="status"
                      aria-hidden="true"
                    ></div>
                  </div>
                ) : airlineListStatus === "error" ? (
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={retry}
                    >
                      Retry
                    </button>{" "}
                    &nbsp;
                    <strong onClick={retry}>Please try again </strong>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        show={typeof deleteAirlineId !== "undefined"}
        handleClose={cancelDelete}
        confirmClose={confirmDelete}
      />
      <AirlineFormCanvas
        show={editId !== ""}
        hide={() => setEditId("")}
        editId={editId}
        Title={editId === "Add" ? "Add Airline" : "Edit Airline"}
        airlineData={airlineList?.results?.find((airline) => airline.id === editId)}
      />
    </>
  );
};

export default ListAirlines;
